import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Row } from "primereact/row";
import { convertLongTimeToTotalMinutes, formatMinutes } from "../../../../app/datetimeUtils";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import TextWithTip from "../../../../components/TextWithTip";
import { AdminReportsResponse, ReportLevel } from "../adminReportsApiSlice";
import { reportSubLevelName } from "../adminReportsUtil";
import { LessonUsageRow } from "./adminUsageReportApiSlice";

export function LessonUsageTable(props: {
	className: string;
	level: ReportLevel;
	fullData: AdminReportsResponse<LessonUsageRow>;
	filteredRows: LessonUsageRow[];
	hideRows: boolean;
	setDistrictFilter: (districtId: number) => void;
	setSchoolFilter: (schoolId: number) => void;
}) {
	const hasData = props.filteredRows.length > 0;

	function changeFilters(id: number) {
		if (props.level === ReportLevel.State) {
			props.setDistrictFilter(id);
		} else if (props.level === ReportLevel.District) {
			props.setSchoolFilter(id);
		}
	}

	const nameTemplate = (rowData: LessonUsageRow) => {
		return (
			<a
				className={`${props.level === ReportLevel.School ? "" : "link"}`}
				onClick={() => changeFilters(rowData.id)}>
				{rowData.name}
			</a>
		);
	};

	const teacherTemplate = (rowData: LessonUsageRow) => {
		return (
			<span>
				<TextWithTip uniqueName={`teachers-${rowData.id}`} text={rowData.teachers} />
			</span>
		);
	};

	const timeSpentTemplate = (rowData: LessonUsageRow) => {
		return formatMinutes(rowData.totalMinutes);
	};

	const footerGroup = (
		<ColumnGroup>
			<Row>
				<Column
					footer={`${props.level}-Wide`}
					footerClassName="font-medium !text-right"
					colSpan={props.level === ReportLevel.School ? 2 : 1}
				/>
				<Column
					footer={formatMinutes(
						convertLongTimeToTotalMinutes(props.fullData.totals.timeSpent)
					)}
					footerClassName="font-medium"
				/>
			</Row>
		</ColumnGroup>
	);

	return (
		<>
			<DataTable
				value={props.hideRows ? [] : props.filteredRows}
				dataKey="id"
				loading={props.fullData.rows == undefined}
				emptyMessage="No results found"
				pt={{
					// Targets the container of the "No results found" message for hiding it
					emptyMessage: {
						style: { display: !props.hideRows ? "table-row" : "none" },
					},
				}}
				stripedRows
				sortIcon={CustomSortIcon}
				className={`block tbl tbl-data tbl-datatable min-w-[40rem] cursor-default ${props.className}`}
				footerColumnGroup={hasData && footerGroup}>
				<Column
					header={reportSubLevelName(props.level)}
					body={nameTemplate}
					sortable
					sortField="name"></Column>
				{props.level === ReportLevel.School && (
					<Column
						header="Teacher(s)"
						body={teacherTemplate}
						className="!max-w-52"
						sortable
						sortField="teachers"></Column>
				)}
				<Column
					header="Time Spent on Lessons"
					body={timeSpentTemplate}
					sortable
					sortField="totalMinutes"></Column>
			</DataTable>
			{props.fullData.rows.length !== props.filteredRows.length && (
				<p className="mt-2 mb-6 px-6 text-sm text-gray-700">
					Showing <span className="font-medium">{props.filteredRows.length}</span>{" "}
					{reportSubLevelName(props.level).toLowerCase()}
					{props.filteredRows.length === 1 ? "" : "s"}
					<span>
						{" "}
						(filtered from{" "}
						<span className="font-medium">{props.fullData.rows.length}</span> total{" "}
						{reportSubLevelName(props.level).toLowerCase()}
						{props.fullData.rows.length === 1 ? "" : "s"})
					</span>
				</p>
			)}
		</>
	);
}
